import React, { memo, useLayoutEffect, useRef, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';

// [FEEDBACK] Modernized Node Design
// Removed hardcoded "chon-seureoum" colors.
// Using gradients, glassmorphism, and softer shadows.

import { categoryColors } from '../../utils/layout-helper';

function CustomMindmapNode({ data, selected }) {
    const { label, group, isHighlighted, theme } = data;
    const labelRef = useRef(null);

    // Define base styles based on group
    const nodeStyles = useMemo(() => {
        let styles = {
            container: '',
            text: '',
            image: '',
            handle: 'w-3 h-3 bg-gray-400 dark:bg-gray-500',
        };

        const isDark = theme === 'dark';

        switch (group) {
            case 0: // Root Node (AI)
                styles.container = isDark
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 border-blue-400 shadow-blue-900/50'
                    : 'bg-gradient-to-br from-blue-500 to-indigo-600 border-blue-300 shadow-blue-200';
                styles.text = 'font-extrabold text-white text-6xl tracking-tight'; // Increased font size to 6xl
                styles.image = 'max-w-[375px] max-h-[375px]'; // Increased by 1.5x (250 → 375)
                styles.size = 'w-[675px] h-[675px] rounded-full border-8'; // Increased size by 1.5x (450 → 675)
                break;

            case 1: // Major Categories (e.g., Development, Education)
                // [FEEDBACK] Dynamic Colors for 1st Level Nodes
                const colorClass = categoryColors[data.id] || (isDark ? 'from-slate-700 to-slate-800' : 'from-white to-gray-50');

                styles.container = isDark
                    ? `bg-gradient-to-br ${colorClass} border-white/10 shadow-lg`
                    : `bg-gradient-to-br ${colorClass} border-gray-200 shadow-xl`;

                styles.text = 'font-bold text-white text-3xl shadow-black/20 drop-shadow-md'; // Increased to text-3xl (from 2xl)
                styles.image = 'max-w-[120px] max-h-[120px] mb-3 drop-shadow-md'; // Increased by 1.5x (80 → 120)
                styles.size = 'min-w-[360px] min-h-[210px] rounded-2xl border-2 p-5'; // Increased by 1.5x (240 → 360, 140 → 210)
                break;

            case 2: // Sub Categories
                styles.container = isDark
                    ? 'bg-slate-800 border-slate-500 shadow-slate-900/40 hover:border-blue-400 transition-colors'
                    : 'bg-white border-gray-300 shadow-lg hover:border-blue-400 transition-colors';
                styles.text = isDark ? 'font-bold text-gray-100 text-xl' : 'font-bold text-gray-800 text-xl';
                styles.image = 'max-w-[56px] max-h-[56px] mb-2';
                styles.size = 'min-w-[180px] min-h-[110px] rounded-xl border p-3';
                break;

            case 3: // Leaf Nodes (Specific Services - if used here)
                styles.container = isDark
                    ? 'bg-slate-800 border-slate-600'
                    : 'bg-white border-gray-200';
                styles.text = isDark ? 'text-base text-gray-200' : 'text-base text-gray-700';
                styles.image = 'max-w-[40px] max-h-[40px]';
                styles.size = 'w-auto px-5 py-3 rounded-lg border shadow-sm';
                break;

            default:
                styles.container = 'bg-white border-gray-200';
                styles.text = 'text-gray-800';
                styles.size = 'w-48 h-24 rounded-lg border';
        }

        // Highlight & Selection
        if (selected) {
            styles.container += ' ring-4 ring-blue-500/60 ring-offset-2 dark:ring-offset-slate-900';
        }
        if (isHighlighted) {
            styles.container += ' ring-4 ring-purple-500/60 ring-offset-2 dark:ring-offset-slate-900 scale-110 transition-transform';
        }

        return styles;
    }, [group, theme, selected, isHighlighted, data.id]);

    return (
        <div
            className={`
        relative flex flex-col items-center justify-center text-center overflow-hidden
        transition-all duration-300 ease-out
        hover:shadow-2xl hover:-translate-y-1 hover:z-10
        ${nodeStyles.container} ${nodeStyles.size}
      `}
        >
            {/* Handles */}
            {group !== 0 && (
                <Handle type="target" position={data.targetPosition || Position.Top} className={nodeStyles.handle} />
            )}
            {group !== 0 && group !== 3 && (
                <Handle type="source" position={data.sourcePosition || Position.Bottom} className={nodeStyles.handle} />
            )}
            {group === 0 && (
                <>
                    <Handle type="source" position={Position.Top} id="top" className={nodeStyles.handle} />
                    <Handle type="source" position={Position.Right} id="right-source" className={nodeStyles.handle} />
                    <Handle type="source" position={Position.Bottom} id="bottom" className={nodeStyles.handle} />
                    <Handle type="source" position={Position.Left} id="left-source" className={nodeStyles.handle} />
                </>
            )}

            {/* Content */}
            <div className="flex flex-col items-center justify-center w-full h-full p-2 z-10">
                {data.imageUrl && (
                    <img
                        src={data.imageUrl}
                        alt={`${label} 로고`}
                        className={`${nodeStyles.image} object-contain drop-shadow-sm`}
                        onError={e => { e.target.onerror = null; e.target.src = '/Images/Logos/Base.jpg'; }}
                    />
                )}
                <div className={`${nodeStyles.text} leading-tight break-words w-full`}>
                    {label}
                </div>
            </div>

            {/* Decorative Gradient Overlay for Root Node */}
            {group === 0 && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none rounded-full" />
            )}
        </div>
    );
}

export default memo(CustomMindmapNode);
